/* import __COLOCATED_TEMPLATE__ from './begin-trial-guide-lib.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type CardlessTrialService from 'embercom/services/cardless-trial-service';
import type GuideLibraryService from 'embercom/services/guide-library-service';
import {
  percentDiscountForSolution,
  PRICING_5_X_EARLY_STAGE_SOLUTION_ID,
} from 'embercom/lib/billing';
import { action } from '@ember/object';
import type TrialExtensionModalService from 'embercom/services/trial-extension-modal-service';
import type CardlessConversionModalService from 'embercom/services/cardless-conversion-modal-service';
import type CompellingOfferCampaignService from 'embercom/services/compelling-offer-campaign-service';

const WARNING_COLORS = 'bg-notice-container border-notice-border';
const PAYWALL_COLORS = 'bg-paywall-container border-paywall-border';

interface Args {
  showUpgradeButton: boolean;
  onUpgrade: () => void;
  transitionToOnboardingHome: () => void;
  showContinueButton: boolean;
  validEarlyStageApplicant: boolean;
  transitionToEarlyStageCheckout: () => void;
  canUseEarlyStageDetection: boolean;
  triggerAnalytics: () => void;
  upgradeButtonLabel: string;
  trialPlanName: string;
  isEligibleForTrialExtension: boolean;
}

interface Signature {
  Args: Args;
  Element: never;
}

export default class BeginTrialGuideLib extends Component<Signature> {
  @service declare guideLibraryService: GuideLibraryService;
  @service declare cardlessTrialService: CardlessTrialService;
  @service declare appService: AppService;
  @service declare trialExtensionModalService: TrialExtensionModalService;
  @service declare purchaseAnalyticsService: $TSFixMe;
  @service declare cardlessConversionModalService: CardlessConversionModalService;
  @service declare compellingOfferCampaignService: CompellingOfferCampaignService;

  get freeSeatsCampaignMetadata() {
    return this.compellingOfferCampaignService.freeSeatsCampaignMetadata;
  }

  get shouldShowFreeSeatsCampaignBanner() {
    return this.compellingOfferCampaignService.isFreeSeatsCampaign;
  }

  get earlyStageDiscount() {
    return percentDiscountForSolution(PRICING_5_X_EARLY_STAGE_SOLUTION_ID, 1);
  }

  get isStandaloneApp() {
    return this.cardlessTrialService.isStandaloneApp;
  }

  get inSalesProspectTrial() {
    // this is only used in the TrialDaysLeft if `isStandaloneApp` is true
    // This will be true if the customer is on a trial set up via billing admin and has not moved to self-serve
    return this.cardlessTrialService.inSalesProspectTrial;
  }

  get daysLeftOfTrial() {
    return this.cardlessTrialService.daysLeftOfTrial;
  }

  // for standalone apps, when fin is live and trial is nearing its end show a warning banner
  get showWarning() {
    let trialEnding = this.daysLeftOfTrial <= 3 && this.daysLeftOfTrial >= 0;
    return this.isStandaloneApp && this.appService.mainStandalonePlatform.finIsLive && trialEnding;
  }

  get bannerColor() {
    if (this.showWarning) {
      return WARNING_COLORS;
    } else {
      return PAYWALL_COLORS;
    }
  }

  @action
  openConversionModal() {
    this.cardlessConversionModalService.toggle();
  }

  @action
  openConversionModalWithTracking() {
    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      object: 'view_other_plans',
      context: 'cardless_trial',
      place: 'enter_demo_banner',
    });
    this.cardlessConversionModalService.toggle();
  }

  @action
  openTrialExtensionSurveyDialog() {
    this.trialExtensionModalService.openModal();
  }

  @action
  trackTrialExtensionCtaRender() {
    this.purchaseAnalyticsService.trackEvent({
      action: 'viewed',
      object: 'open_trial_extension_modal_button',
      context: 'cardless_trial',
      place: 'active_trial_banner',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Banners::BeginTrialGuideLib': typeof BeginTrialGuideLib;
  }
}
